import React, { useState, useEffect } from "react";
import cookie from "react-cookies";

import useAPI from "services/Api";
import Input from "input/Input";
import Page from "pages/Page";
import { ModularHeaderConfig } from "pages/ModularHeader";
import { navigate } from "utility/NavigationService";
import { useNotificationManager } from "notifications/NotificationManager";

const ChangePassword = ({ componentName = "[ChangePassword]" }) => {
    const [changed, setChanged] = useState(false);
    const [clear, setClear] = useState(false);
    const [password1, setPassword1] = useState(null);
    const [password2, setPassword2] = useState(null);

    const API = useAPI();
    const notificationManager = useNotificationManager();
    const user = cookie.load("user") ? cookie.load("user") : null;

    // ---=== Utilities ===--- //

    const goToUser = () => {
        void navigate(`/user`);
    };

    const checkErrors = () => {
        if (!password1 || !password2 || password1.length < 8 || password2.length < 8) {
            notificationManager.addError("Password must be at least 8 characters long.");
            return false;
        }
        else if (password1 !== password2) {
            notificationManager.addError("Entered passwords do not match.");
            return false;
        }

        return true;
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        notificationManager.clearAllNotifications()
        setClear(true);

        if (!checkErrors()) {
            return;
        }

        void API.api_Post(
            componentName,
            "/password/change/",
            JSON.stringify({ new_password1: password1, new_password2: password2 }),
            notificationManager,
            (_response) => {
                setChanged(true);
            },
            (error) => {
                console.error(error.response);
                notificationManager.addError(error);
            }
        );
    };

    // ---=== useEffects ===--- //

    useEffect(() => {
        if (clear) {
            setClear(false);
        }
    }, [clear]);

    // ---=== Render ===--- //

    const getModularHeaderConfig = (): ModularHeaderConfig => {
        return {
            trail: breadCrumbLinks(),
            body: {
                subheader: "Change Password",
                name: user ? `${user.first_name} ${user.last_name}` : "",
                buttons: [
                    {
                        title: "back",
                        click: goToUser,
                        img: "/icons/flip-backward.svg"
                    }
                ]
            }
        };
    };

    const breadCrumbLinks = () => {
        return [
            { label: user ? `${user.first_name} ${user.last_name}` : "User Profile", url: `/user` },
            { label: "Change Password", url: "/password/change" }
        ];
    };

    return (
        <Page
            componentName={componentName}
            headerConfig={getModularHeaderConfig()}
        >
            {
                !changed
                    ? (
                        <div className="h-screen flex items-center justify-center pb-12">
                            <div className="w-96 h-96 flex-col justify-center content-center items-center gap-8 inline-flex">
                                <div className="self-stretch h-28 flex-col justify-start items-center gap-6 flex">
                                    <div className="w-14 h-14 p-3.5 rounded-xl shadow border border-gray-50 justify-center items-center inline-flex">
                                        <img src="/icons/lock-01.svg" className="w-7 h-7 relative flex-col justify-start items-start flex" alt="Lock Icon"/>
                                    </div>
                                    <div className="self-stretch h-9 flex-col justify-start items-start gap-3 flex">
                                        <div className="self-stretch text-center text-gray-900 text-3xl font-semibold leading-9">Set a password</div>
                                    </div>
                                </div>
                                <form className="w-full max-w-sm" onSubmit={handleSubmit}>
                                    <div className="self-stretch h-56 rounded-xl flex-col justify-start items-center gap-6 flex">
                                        <div className="self-stretch h-40 flex-col justify-start items-start gap-5 flex">
                                            <div className="self-stretch h-16 flex-col justify-start items-start gap-1.5 flex">
                                                <div className="self-stretch h-16 flex-col justify-start items-start gap-1.5 flex">
                                                    <div className="text-gray-700 text-sm font-medium leading-tight">Password</div>
                                                    <Input
                                                        className={"self-stretch px-3.5 py-2.5 bg-white rounded-lg shadow border border-gray-300 justify-start items-center gap-2 inline-flex grow shrink basis-0 text-gray-500 text-base font-normal leading-normal"}
                                                        name="Password"
                                                        noLabel
                                                        required
                                                        type="password"
                                                        validations={[{ type: "minLength", value: 8 }]}
                                                        value={password1}
                                                        onChange={setPassword1}
                                                    />
                                                </div>
                                            </div>
                                            <div className="self-stretch h-16 flex-col justify-start items-start gap-1.5 flex">
                                                <div className="self-stretch h-16 flex-col justify-start items-start gap-1.5 flex">
                                                    <div className="text-gray-700 text-sm font-medium leading-tight">Confirm password</div>
                                                    <Input
                                                        className={"self-stretch px-3.5 py-2.5 bg-white rounded-lg shadow border border-gray-300 justify-start items-center gap-2 inline-flex grow shrink basis-0 text-gray-500 text-base font-normal leading-normal"}
                                                        name="Password"
                                                        noLabel
                                                        required
                                                        type="password"
                                                        validations={[{ type: "minLength", value: 8 }]}
                                                        value={password2}
                                                        onChange={setPassword2}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <button onClick={handleSubmit} className="self-stretch px-4 py-2.5 bg-blue-500 hover:bg-blue-600 rounded-lg shadow border-2 border-blue-600 justify-center items-center gap-1.5 inline-flex text-white text-base font-semibold leading-normal cursor-pointer">
                                            Set Password
                                        </button>
                                    </div>
                                </form>
                                <a href="/user">
                                    <div className="justify-center items-center gap-1.5 inline-flex">
                                        <img src="/icons/arrow-left.svg" className="w-5 h-5 relative" alt="Left Arrow"/>
                                        <div className="text-gray-600 text-sm font-semibold leading-tight">Go to profile</div>
                                    </div>
                                </a>
                            </div>
                        </div>
                    )
                    : (
                        <div className="h-screen flex items-center justify-center pb-12">
                            <div className="w-96 h-52 flex-col justify-start items-center gap-8 inline-flex">
                                <div className="self-stretch h-40 flex-col justify-start items-center gap-6 flex">
                                    <div className="w-14 h-14 p-3.5 rounded-xl shadow border border-gray-50 justify-center items-center inline-flex">
                                        <img src="/icons/check-circle.svg" className="w-7 h-7 relative flex-col justify-start items-start flex" alt="Check Circle"/>
                                    </div>
                                    <div className="self-stretch h-20 flex-col justify-start items-start gap-3 flex">
                                        <div className="self-stretch text-center text-gray-900 text-3xl font-semibold leading-9">Password Set</div>
                                        <div className="self-stretch text-center text-gray-600 text-base font-normal leading-normal">Your password has been successfully changed</div>
                                    </div>
                                </div>
                                <a href="/user">
                                    <div className="justify-center items-center gap-1.5 inline-flex">
                                        <img src="/icons/arrow-left.svg" className="w-5 h-5 relative" alt="Left Arrow"/>
                                        <div className="text-gray-600 text-sm font-semibold leading-tight">Go to profile</div>
                                    </div>
                                </a>
                            </div>
                        </div>
                    )
            }
        </Page>
    );
};

export default ChangePassword;
